/**
 * English: Authenticated fetch helpers (GET/POST/PUT/DELETE) with JSON and Bearer token.
 * Thai: ตัวช่วย fetch ที่มี Bearer token สำหรับ GET/POST/PUT/DELETE และ JSON
 */
import { useCallback } from 'react';
import { API_BASE } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { extractApiError, parseJsonResponse } from '../utils/httpJson';

export function useApi() {
  const { token } = useAuth();

  const headers = useCallback(() => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  const get = useCallback(async (path: string, params: Record<string, string> = {}) => {
    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const reqHeaders = headers();
    if (process.env.NODE_ENV === 'development') {
      console.log('[useApi] GET', url.toString(), 'Auth:', reqHeaders['Authorization'] ? 'Bearer ***' : 'NONE');
    }
    const res = await fetch(url.toString(), { headers: reqHeaders });
    if (process.env.NODE_ENV === 'development') {
      console.log('[useApi] Response status:', res.status);
    }
    const data = await parseJsonResponse(res, { url: url.toString() });
    if (!res.ok) {
      throw new Error(extractApiError(data, `Request failed with status ${res.status}`));
    }
    return data;
  }, [headers]);

  const post = useCallback(async (path: string, body: unknown) => {
    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(body),
    });
    const data = await parseJsonResponse(res, { url: url.toString() });
    if (!res.ok) {
      throw new Error(extractApiError(data, `Request failed with status ${res.status}`));
    }
    return data;
  }, [headers]);

  const del = useCallback(async (path: string, params: Record<string, string> = {}) => {
    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url.toString(), { method: 'DELETE', headers: headers() });
    const data = await parseJsonResponse(res, { url: url.toString() });
    if (!res.ok) {
      throw new Error(extractApiError(data, `Request failed with status ${res.status}`));
    }
    return data;
  }, [headers]);

  const put = useCallback(async (path: string, body: unknown) => {
    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    const res = await fetch(url.toString(), {
      method: 'PUT',
      headers: headers(),
      body: JSON.stringify(body),
    });
    const data = await parseJsonResponse(res, { url: url.toString() });
    if (!res.ok) {
      throw new Error(extractApiError(data, `Request failed with status ${res.status}`));
    }
    return data;
  }, [headers]);

  return { get, post, del, put };
}
